import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Eye, Tag, ArrowLeft, Share2, Clock } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { blogAPI } from '../lib/api';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { trackBlogView } from '../hooks/useAnalytics';

const categoryLabels = {
  'land-registration': { bn: 'জমি রেজিস্ট্রেশন', en: 'Land Registration' },
  'land-records': { bn: 'ভূমি রেকর্ড', en: 'Land Records' },
  'legal-tips': { bn: 'আইনি পরামর্শ', en: 'Legal Tips' },
  'fees-taxes': { bn: 'ফি ও কর', en: 'Fees & Taxes' },
  'land-info': { bn: 'ভূমি তথ্য', en: 'Land Info' },
};

// Calculate reading time
const calculateReadingTime = (content) => {
  if (!content) return 1;
  const wordsPerMinute = 200;
  const wordCount = content.split(/\s+/).length;
  return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
};

const BlogDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const isBn = language === 'bn';

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await blogAPI.getBySlug(slug);
        setPost(res.data);
        // Track blog view
        trackBlogView(slug);
      } catch (err) {
        console.error('Failed to fetch blog post:', err);
      }
      setLoading(false);
    };
    fetchPost();
  }, [slug]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: post.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const renderContent = (content) => {
    if (!content) return null;
    return content.split('\n').map((line, i) => {
      if (line.startsWith('# ')) return <h1 key={i} className={`text-2xl md:text-3xl font-bold mt-8 mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{line.slice(2)}</h1>;
      if (line.startsWith('## ')) return <h2 key={i} className={`text-xl md:text-2xl font-bold mt-6 mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>{line.slice(3)}</h2>;
      if (line.startsWith('### ')) return <h3 key={i} className={`text-lg font-bold mt-4 mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{line.slice(4)}</h3>;
      if (line.startsWith('- ')) return <li key={i} className={`ml-6 mb-1 list-disc ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{renderInline(line.slice(2))}</li>;
      if (line.startsWith('| ')) return null; // Simple table skip
      if (line.match(/^\d+\. /)) return <li key={i} className={`ml-6 mb-1 list-decimal ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{renderInline(line.replace(/^\d+\. /, ''))}</li>;
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className={`mb-3 leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{renderInline(line)}</p>;
    });
  };

  const renderInline = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className={isDark ? 'text-white' : 'text-gray-900'}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} py-12`}>
        <div className="max-w-3xl mx-auto px-4 animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-8" />
          {[1, 2, 3, 4].map(i => <div key={i} className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-3" />)}
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="text-center">
          <p className="text-xl mb-4">{isBn ? 'আর্টিকেল পাওয়া যায়নি' : 'Article not found'}</p>
          <Link to="/blog" className="text-[#2E7D32] font-medium">{isBn ? 'ব্লগে ফিরুন' : 'Back to blog'}</Link>
        </div>
      </div>
    );
  }

  const content = isBn ? post.content : (post.content_en || post.content);
  const readingTime = calculateReadingTime(content);
  const postTitle = isBn ? post.title : (post.title_en || post.title);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <SEO 
        title={post.meta_title || postTitle}
        description={post.meta_description || post.excerpt || post.summary}
        path={`/blog/${slug}`}
        type="article"
        article={{
          publishedTime: post.created_at,
          modifiedTime: post.updated_at,
          section: categoryLabels[post.category]?.en || 'Land Information',
          tags: post.tags || [],
        }}
      />
      <Navbar isDark={isDark} />

      <div className="max-w-3xl mx-auto px-4 py-8 md:py-12 mt-16">
        {/* Back Link */}
        <Link to="/blog" className={`inline-flex items-center gap-2 text-sm mb-6 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>
          <ArrowLeft size={16} />
          {isBn ? 'সব আর্টিকেল' : 'All Articles'}
        </Link>

        {/* Header */}
        <article>
          <div className="flex items-center gap-3 mb-4">
            <Badge className="bg-[#2E7D32] text-white">
              {categoryLabels[post.category]?.[language] || post.category}
            </Badge>
            {post.tags?.slice(0, 3).map(tag => (
              <Badge key={tag} variant="outline" className="text-xs dark:border-gray-600 dark:text-gray-300">
                <Tag size={10} className="mr-1" />{tag}
              </Badge>
            ))}
          </div>

          <h1 className={`text-2xl md:text-4xl font-bold mb-4 leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="blog-detail-title">
            {postTitle}
          </h1>

          <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-200 dark:border-gray-700 flex-wrap">
            <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
              <Calendar size={14} />
              {new Date(post.created_at).toLocaleDateString(isBn ? 'bn-BD' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
            <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
              <Clock size={14} />
              {isBn ? `${readingTime} মিনিট পড়া` : `${readingTime} min read`}
            </div>
            <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
              <Eye size={14} />
              {post.views} {isBn ? 'বার পড়া হয়েছে' : 'views'}
            </div>
            <Button variant="ghost" size="sm" onClick={handleShare} className="ml-auto">
              <Share2 size={14} className="mr-1" /> {isBn ? 'শেয়ার' : 'Share'}
            </Button>
          </div>

          {/* Content */}
          <div className="prose-custom">
            {renderContent(content)}
          </div>
        </article>

        {/* CTA */}
        <div className={`mt-12 p-6 rounded-xl text-center ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-[#E8F5E9] border border-[#2E7D32]/20'}`}>
          <h3 className={`font-bold text-lg mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {isBn ? 'ভূমি বিষয়ে সাহায্য দরকার?' : 'Need help with land matters?'}
          </h3>
          <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {isBn ? 'আমাদের যাচাইকৃত সেবাদাতাদের সাথে যোগাযোগ করুন' : 'Connect with our verified service providers'}
          </p>
          <Link to="/services" className="inline-flex items-center gap-2 bg-[#2E7D32] text-white px-6 py-2.5 rounded-lg font-medium hover:bg-[#1B5E20] transition-colors">
            {isBn ? 'সেবাদাতা খুঁজুন' : 'Find Service Providers'}
          </Link>
        </div>
      </div>
      
      <Footer isDark={isDark} />
    </div>
  );
};

export default BlogDetail;
